import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { TicketDialog } from "@/components/TicketDialog";
import { AiComposeTools } from "@/components/AiComposeTools";
import { AutoTranslateFR } from "@/components/AutoTranslateFR";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CANNED_REPLIES } from "@/lib/cannedReplies";
import { LifeBuoy, Loader2, Send, AlertTriangle, MessageSquareHeart, HelpCircle, ExternalLink, Zap, Clock } from "lucide-react";
import { useI18n } from "@/i18n";

const STATUS = {
  open: { cls: "text-signal-processing border-signal-processing/40 bg-signal-processing/10" },
  in_progress: { cls: "text-aurora-cyan border-aurora-cyan/40 bg-aurora-cyan/10" },
  resolved: { cls: "text-signal-success border-signal-success/40 bg-signal-success/10" },
  closed: { cls: "text-zinc-400 border-white/15 bg-white/5" },
};
const PRIORITY = {
  fast: { cls: "text-amber-300 border-amber-400/40 bg-amber-400/10", Icon: Zap },
  standard: { cls: "text-zinc-400 border-white/15 bg-white/5", Icon: Clock },
};
const KIND_ICON = { problem: AlertTriangle, feedback: MessageSquareHeart, other: HelpCircle };
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export const Tickets = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [slaMin, setSlaMin] = useState(30);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/settings").then(({ data }) => setSlaMin(data.settings?.express_sla_minutes || 30)).catch(() => {});
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isAdmin]);

  const load = useCallback(() => api.get("/tickets").then(({ data }) => setTickets(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const openTicket = async (t) => {
    setSel(t);
    const { data } = await api.get(`/tickets/${t.id}`);
    setSel(data.ticket); setThread(data.messages);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${sel.id}/messages`, { text: reply });
      setReply("");
      const { data } = await api.get(`/tickets/${sel.id}`);
      setSel(data.ticket); setThread(data.messages); load();
    } catch (e) { toast.error(t("tickets.error")); }
    setBusy(false);
  };

  const setStatus = async (status) => {
    const { data } = await api.post(`/admin/tickets/${sel.id}/status`, { status });
    setSel(data); load(); toast.success(t("tickets.statusUpdated"));
  };

  const prioOf = (tk) => (tk.priority === "fast" ? "fast" : "standard");

  const fmtDur = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min`;
    const h = Math.floor(m / 60);
    return `${h}h${String(m % 60).padStart(2, "0")}`;
  };

  // Live SLA chrono for Express tickets (admin only).
  const SlaChrono = ({ tk, big }) => {
    if (!isAdmin || prioOf(tk) !== "fast") return null;
    const slaMs = slaMin * 60000;
    const created = new Date(tk.created_at).getTime();
    const handled = tk.status !== "open" && tk.taken_at ? new Date(tk.taken_at).getTime() : null;
    const elapsed = (handled || now) - created;
    const breached = elapsed > slaMs;
    let cls, label;
    if (handled) {
      cls = breached ? "text-signal-error border-signal-error/40 bg-signal-error/10" : "text-signal-success border-signal-success/40 bg-signal-success/10";
      label = `${t("tickets.takenIn")} ${fmtDur(elapsed)}`;
    } else if (breached) {
      cls = "text-signal-error border-signal-error/50 bg-signal-error/15 animate-pulse";
      label = `${t("tickets.slaBreach")} ${fmtDur(elapsed)}`;
    } else if (elapsed > slaMs * 0.5) {
      cls = "text-amber-300 border-amber-400/40 bg-amber-400/10";
      label = `${t("tickets.openSince")} ${fmtDur(elapsed)}`;
    } else {
      cls = "text-signal-success border-signal-success/30 bg-signal-success/5";
      label = `${t("tickets.openSince")} ${fmtDur(elapsed)}`;
    }
    return (
      <span data-testid={`sla-${tk.id}`} className={`font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${big ? "text-[10px] px-2 py-1" : "text-[9px]"} ${cls}`}>
        <Clock className={big ? "w-3 h-3" : "w-2.5 h-2.5"} /> {label}
      </span>
    );
  };

  const renderRow = (tk) => {
    const Icon = KIND_ICON[tk.kind] || HelpCircle;
    const active = sel?.id === tk.id;
    const prio = prioOf(tk);
    const PIcon = PRIORITY[prio].Icon;
    return (
      <button key={tk.id} data-testid={`ticket-row-${tk.id}`} onClick={() => openTicket(tk)}
        className={`relative w-full text-left px-4 py-3.5 border-b border-white/5 last:border-0 transition-colors ${active ? "bg-aurora-cyan/10" : "hover:bg-white/5"}`}>
        {isAdmin && prio === "fast" && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-amber-400" />}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-mono text-[10px] text-aurora-cyan">{tk.ref}</span>
          <div className="flex items-center gap-1.5">
            {isAdmin && (
              <span data-testid={`ticket-priority-${tk.id}`} className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${PRIORITY[prio].cls}`}>
                <PIcon className="w-2.5 h-2.5" /> {t(`tickets.prio_${prio}`)}
              </span>
            )}
            <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS[tk.status]?.cls}`}>{t(`tickets.${tk.status}`)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-sm truncate"><Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" /> {tk.subject}</div>
        {isAdmin && prio === "fast" && <div className="mt-1.5"><SlaChrono tk={tk} /></div>}
        <div className="font-mono text-[10px] text-zinc-500 mt-1 truncate">
          {isAdmin ? `${tk.user_name} · ` : ""}{tk.vehicle || "—"} · {fmt(tk.created_at)}
        </div>
      </button>
    );
  };

  const SectionHeader = ({ kind, count }) => (
    <div data-testid={`ticket-queue-${kind}`} className={`flex items-center gap-2 px-4 py-2.5 border-b border-white/10 ${kind === "fast" ? "bg-amber-400/10" : "bg-white/[0.03]"}`}>
      {kind === "fast" ? <Zap className="w-3.5 h-3.5 text-amber-300 animate-pulse" /> : <Clock className="w-3.5 h-3.5 text-zinc-400" />}
      <span className={`font-display text-xs font-bold uppercase tracking-widest ${kind === "fast" ? "text-amber-300" : "text-zinc-400"}`}>
        {kind === "fast" ? t("tickets.queueFast") : t("tickets.queueStandard")}
      </span>
      <span className="ml-auto font-mono text-[10px] text-zinc-500">{count}</span>
    </div>
  );

  const fastT = tickets.filter((tk) => prioOf(tk) === "fast");
  const stdT = tickets.filter((tk) => prioOf(tk) === "standard");

  return (
    <AppShell mode={isAdmin ? "admin" : "client"}>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">{t("tickets.kicker")}</div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("tickets.title")} {isAdmin ? t("tickets.workshop") : ""}</h1>
        </div>
        {!isAdmin && <TicketDialog onCreated={load} />}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 glass rounded-md overflow-hidden self-start">
          {loading ? (
            <div className="py-12 text-center font-mono text-xs text-zinc-500">{t("tickets.loading")}</div>
          ) : tickets.length === 0 ? (
            <div className="py-16 text-center px-6">
              <LifeBuoy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">{isAdmin ? t("tickets.none") : t("tickets.noneClient")}</p>
            </div>
          ) : isAdmin ? (
            <div>
              {fastT.length > 0 && <><SectionHeader kind="fast" count={fastT.length} />{fastT.map(renderRow)}</>}
              {stdT.length > 0 && <><SectionHeader kind="standard" count={stdT.length} />{stdT.map(renderRow)}</>}
            </div>
          ) : tickets.map(renderRow)}
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {!sel ? (
            <div className="glass rounded-md py-20 text-center text-zinc-500 text-sm">{t("tickets.select")}</div>
          ) : (
            <div className="glass rounded-md p-6" data-testid="ticket-detail">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="font-mono text-[10px] text-aurora-cyan mb-1">{sel.ref} · {fmt(sel.created_at)}</div>
                  <h2 className="font-display text-xl font-bold uppercase tracking-tight">{sel.subject}</h2>
                  <div className="font-mono text-[10px] text-zinc-500 mt-1">{sel.user_name} · {sel.vehicle || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && <SlaChrono tk={sel} big />}
                  {isAdmin && (
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border flex items-center gap-1 ${PRIORITY[prioOf(sel)].cls}`}>
                      {React.createElement(PRIORITY[prioOf(sel)].Icon, { className: "w-3 h-3" })} {t(`tickets.prio_${prioOf(sel)}`)}
                    </span>
                  )}
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${STATUS[sel.status]?.cls}`}>{t(`tickets.${sel.status}`)}</span>
                </div>
              </div>

              {isAdmin && (
                <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-white/5">
                  {Object.keys(STATUS).map((s) => (
                    <button key={s} data-testid={`ticket-status-${s}`} onClick={() => setStatus(s)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${sel.status === s ? STATUS[s].cls : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                      {t(`tickets.${s}`)}
                    </button>
                  ))}
                  {sel.order_id && (
                    <a href={`/admin/orders/${sel.order_id}`} className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-aurora-purple hover:underline">
                      <ExternalLink className="w-3 h-3" /> {t("tickets.viewOrder")}
                    </a>
                  )}
                </div>
              )}

              <div className="space-y-3 max-h-72 overflow-y-auto pr-1 mb-4" data-testid="ticket-thread">
                {thread.map((m) => {
                  const mine = (isAdmin && m.sender_role === "admin") || (!isAdmin && m.sender_role === "client");
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[82%] rounded-lg px-3.5 py-2.5 ${mine ? "bg-aurora-cyan/15 border border-aurora-cyan/30" : "bg-white/5 border border-white/10"}`}>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                          {m.sender_role === "admin" ? `${t("tickets.workshopSender")} · ${m.sender_name}` : m.sender_name} · {fmt(m.created_at)}
                        </div>
                        <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words">{m.text}</p>
                        {isAdmin && m.sender_role === "client" && (sel.client_lang || "").toLowerCase() !== "fr" && (
                          <AutoTranslateFR text={m.text} msgId={m.id} source="ticket" cachedFr={m.text_fr} cachedIsFr={m.is_fr} cachedLang={m.src_lang} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {sel.status !== "closed" && (
                <div className="space-y-2">
                  {isAdmin && (
                    <div className="flex flex-wrap gap-1.5" data-testid="canned-replies">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 self-center mr-1">Réponses rapides :</span>
                      {CANNED_REPLIES.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`canned-reply-${c.id}`}
                          onClick={() => setReply(c.text)}
                          title="Insérer ce modèle (modifiable avant envoi)"
                          className="px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide rounded-full border border-white/10 text-zinc-300 hover:border-aurora-cyan hover:text-aurora-cyan transition-colors"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea data-testid="ticket-reply-input" rows={reply.includes("\n") ? 6 : 1} value={reply} onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder={t("tickets.replyPh")} className="flex-1 resize-none bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
                    {isAdmin && <AiComposeTools text={reply} onApply={setReply} clientLang={sel.client_lang || ""} disabled={busy} />}
                    <button data-testid="ticket-reply-btn" onClick={sendReply} disabled={busy || !reply.trim()}
                      className="h-[42px] px-4 bg-aurora-cyan text-white rounded-sm flex items-center disabled:opacity-50">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
};
